import { GraphQLIndicatorRepository } from "@app/repositories/GraphQLRepositories/indicator/IndicatorRepository";
import { GraphQLObjectiveTargetRepository } from "@app/repositories/GraphQLRepositories/ObjectiveTargetRepository";
import { tanstackUnitaryOptimisticUpdateOptionsMaker } from "@app/shared/utils/optimisticUpdates";
import { Indicator2Filter } from "@generated/client/graphql";
import {
    useMutation,
    useMutationState,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { objectiveKeys } from "../data";
import {
    makeOptimisticObjectiveTargetsUpdateOnManyTargetsDelete,
    makeOptimisticObjectiveTargetsUpdateOnTargetCreate,
    makeOptimisticObjectiveTargetsUpdateOnTargetDelete,
} from "./services";

export const objectiveTargetKeys = {
    update: () =>
        [...objectiveKeys.updateObjective(), "updateObjectiveTarget"] as const,
    create: () =>
        [...objectiveKeys.updateObjective(), "createObjectiveTarget"] as const,
    delete: () =>
        [...objectiveKeys.updateObjective(), "deleteObjectiveTarget"] as const,
    deleteMany: () =>
        [...objectiveKeys.updateObjective(), "deleteObjectiveTargets"] as const,
};

const objectiveTargetRepository = new GraphQLObjectiveTargetRepository();
const indicatorRepository = new GraphQLIndicatorRepository();

export const useUpdateObjectiveTarget = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationKey: objectiveTargetKeys.update(),
        mutationFn: objectiveTargetRepository.update,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: objectiveKeys.all(),
            });
        },
    });

    return mutation;
};

export const useCreateObjectiveTarget = (objectiveId: string) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationKey: objectiveTargetKeys.create(),
        mutationFn: objectiveTargetRepository.create,
        ...tanstackUnitaryOptimisticUpdateOptionsMaker(
            () => objectiveKeys.objectiveDetails(objectiveId),
            makeOptimisticObjectiveTargetsUpdateOnTargetCreate(objectiveId),
        ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: objectiveKeys.objectiveDetails(objectiveId),
            });
        },
    });

    return mutation;
};

export const useDeleteObjectiveTarget = (objectiveId: string) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationKey: objectiveTargetKeys.delete(),
        mutationFn: objectiveTargetRepository.delete,
        ...tanstackUnitaryOptimisticUpdateOptionsMaker(
            () => objectiveKeys.objectiveDetails(objectiveId),
            makeOptimisticObjectiveTargetsUpdateOnTargetDelete(objectiveId),
        ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: objectiveKeys.all(),
            });
        },
    });

    return mutation;
};

export const useDeleteObjectiveTargets = (objectiveId: string) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationKey: objectiveTargetKeys.deleteMany(),
        mutationFn: objectiveTargetRepository.deleteMany,
        ...tanstackUnitaryOptimisticUpdateOptionsMaker(
            () => objectiveKeys.objectiveDetails(objectiveId),
            makeOptimisticObjectiveTargetsUpdateOnManyTargetsDelete(
                objectiveId,
            ),
        ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: objectiveKeys.all(),
            });
        },
    });

    return mutation;
};

export const useIndicatorsFiltered = (
    filters: Indicator2Filter,
    entityId: string,
) => {
    const { data, isPending } = useQuery({
        queryKey: objectiveKeys.indicatorsFiltered(filters, entityId),
        queryFn: () => indicatorRepository.getFiltered(filters, entityId),
    });

    return { indicators: data, isPending };
};

export const useUpdateObjectiveStatus = () => {
    const status = useMutationState({
        filters: {
            mutationKey: [...objectiveKeys.updateObjective()],
        },
        select: (mutation) => mutation.state.status,
    });

    return status;
};
